import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld } from '../grid.ts';
import { dist2, idiv, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { onHull } from '../battle/bote-boat.ts';
import { wakeFork, wakeOrder } from '../battle/wake.ts';
import { swellOrder, swellWarn } from '../battle/swell.ts';
import { useSkills } from './ghost.ts';
import { islaBot } from './isla-bot.ts';

/**
 * The lab's bots for the Long Hunt's chapter 3 (docs/sendas/barbaro.md, «El bote»; docs/SENDAS.md, «Calibración»),
 * asked by ai/vert-bot.ts:
 *
 * - the wake (`wake`): who knows taps the channel with the Wolf's wake the current doesn't close, and keeps the wolves
 *   off the oars (those at a rowlock first); who doesn't leaves Bera the widest (Ivar's way) and fights what is nearest.
 * - the swell (`swell`): who knows holds to a shield as the bow rises, keeps the wolves off the people before a wave,
 *   hauls back whoever the water takes, and swings over to a floe where a band waits; who doesn't fights what is nearest
 *   and lets the waves come.
 * - the old howler (`elder`): who knows, as it looks at the water, runs for that tip (or stays on it if close), steps out
 *   of the leap's shadow, and otherwise fights it with the pack off the hull; who doesn't fights what is nearest.
 *
 * It only moves the hero, taps what a player would and picks its target; nothing it does is outside the rules.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/** What the hero can reach from where it stands (aboard: the hull's; the flying ones anywhere). */
const reachable = (s: BattleState, e: Enemy, cells: number[] | null): boolean => {
  if (!targetable(s, e)) return false;
  if (!cells || NIGHTMARES[e.kind].flying) return true;
  const c = cellAtWorld(s, e.x, e.y);
  return cells.includes(c);
};

/* ---------- the wake ---------- */

const wakeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.bote?.wake;
  const w = s.senda?.bote?.wake;
  if (!r || !w || w.done >= 0) return;
  const f = wakeFork(s);
  if (f && knows && w.pick !== f.right) wakeOrder(s, h, f.right);
  // The wolves at a rowlock first (the oars), then what's aboard, nearest.
  const aboard = (e: Enemy): boolean => targetable(s, e) && (NIGHTMARES[e.kind].flying || onHull(r.boat, cellAtWorld(s, e.x, e.y)));
  if (knows) {
    const at = w.biters.map((b) => findEnemy(s, b[0])).filter((e): e is Enemy => !!e && e.hp > 0 && aboard(e));
    const t = nearestTo(at, h.x, h.y) ?? nearestTo(enemiesNear(s, h.x, h.y, 16000, aboard), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return go(s, h, 0, 0, 600);
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, aboard), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
};

/* ---------- the swell ---------- */

const nearestShield = (shields: Pt[], x: number, y: number): Pt => {
  let best = shields[0] as Pt;
  let bd = 0x7fffffff;
  for (const p of shields) {
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

const swellBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.bote?.swell;
  const w = s.senda?.bote?.swell;
  if (!r || !w || w.done >= 0 || w.to !== -1 || h.stun > 0) return;
  const deck = r.boat.cells;
  if (!knows) {
    // Back aboard from a floe it found itself on (it has to: nothing to fight there for long).
    if (w.on >= 0 && !enemiesNear(s, h.x, h.y, 2500, (e) => targetable(s, e)).length) return swellOrder(s, h, 0);
    const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => reachable(s, e, w.on >= 0 ? null : deck)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return;
  }
  // The bow rising: aboard, to the nearest shield and hit what comes at it from there.
  if (swellWarn(s) && w.on < 0) {
    const p = nearestShield(r.boat.shields, h.x, h.y);
    if (!withinRange(p.x, p.y, h.x, h.y, r.hold - 250)) {
      h.target = 0;
      h.forced = false;
      orderMove(s, h, p.x, p.y);
      return;
    }
    h.path = [];
    h.moving = false;
    h.destX = h.x;
    h.destY = h.y;
    const t = nearestTo(enemiesNear(s, h.x, h.y, 1100, (e) => targetable(s, e)), h.x, h.y);
    h.target = t ? t.id : 0;
    return;
  }
  // Someone in the water: to the gunwale by them.
  const sw = w.swept.find((p) => !p.lost && p.until >= 0);
  if (sw) {
    if (w.on >= 0) return swellOrder(s, h, 0);
    h.target = 0;
    h.forced = false;
    return go(s, h, sw.x, sw.y);
  }
  // On a floe: clear it, then back.
  if (w.on >= 0) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 3000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    return swellOrder(s, h, 0);
  }
  // Wolves at the people first (they'd be swept), then what's aboard; a band on a floe, over to it when the deck is clear.
  const people = (s.senda?.npcs ?? []).filter((n) => n.hp > 0 && !n.away && n.id !== 'boat' && n.id !== 'terron');
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const n of people)
    for (const e of enemiesNear(s, n.x, n.y, 1200, (x) => reachable(s, x, deck))) {
      const d = dist2(e.x, e.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
  if (best) return strike(s, h, best);
  const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (e) => reachable(s, e, deck)), h.x, h.y);
  if (t) return strike(s, h, t);
  const next = r.plan[w.wave];
  const calm = next === undefined || next - s.waveT > r.rope + 8 * 12;
  const q = w.waiting[0];
  if (q && calm) return swellOrder(s, h, q[1] + 1);
  h.target = 0;
  go(s, h, 0, 0, 600);
};

/* ---------- the old howler ---------- */

const elderBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.bote?.elder;
  const w = s.senda?.bote?.elder;
  if (!r || !w || w.done >= 0) return;
  const e = findEnemy(s, w.id);
  // The leap's shadow on it: out of it (who knows, and who doesn't: anyone sees a shadow).
  if (w.leapT >= 0 && withinRange(h.x, h.y, w.leapAt.x, w.leapAt.y, r.leapR + 200)) {
    const dx = h.x - w.leapAt.x || 1;
    const dy = h.y - w.leapAt.y;
    const l = Math.abs(dx) + Math.abs(dy) || 1;
    h.target = 0;
    h.forced = false;
    return go(s, h, h.x + idiv(dx * 1700, l), h.y + idiv(dy * 1700, l), 200);
  }
  if (knows && e && e.hp > 0 && (w.phase === 1 || w.phase === 2) && w.tip >= 0) {
    const t = r.tips[w.tip] as Pt;
    // Right on it as it sets off: stay on it, blow after blow; else run for the tip and wait there.
    if (withinRange(h.x, h.y, e.x, e.y, 1000) && w.phase === 2) return strike(s, h, e);
    if (!withinRange(h.x, h.y, t.x, t.y, r.block - 400)) {
      h.target = 0;
      h.forced = false;
      return go(s, h, t.x, t.y, 200);
    }
    h.path = [];
    h.moving = false;
    if (withinRange(h.x, h.y, e.x, e.y, 1000)) return strike(s, h, e);
    h.target = 0;
    return;
  }
  if (knows) {
    // The pack off the hull first (the boat), then the old one.
    const gn = nearestTo(enemiesNear(s, h.x, h.y, 16000, (x) => targetable(s, x) && x.id !== w.id && onHull(r.boat, cellAtWorld(s, x.x, x.y))), h.x, h.y);
    if (gn) return strike(s, h, gn);
    if (e && e.hp > 0 && w.phase !== 3) return strike(s, h, e);
    const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (x) => targetable(s, x) && x.id !== w.id), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return go(s, h, 0, 0, 600);
  }
  const t = nearestTo(enemiesNear(s, h.x, h.y, 16000, (x) => targetable(s, x) && !(x.id === w.id && w.phase === 3)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
};

/** The chapter's act this bot plays (false: not one of its). */
export const boteBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda?.bote;
  if (r?.wake) wakeBot(s, h, knows);
  else if (r?.swell) swellBot(s, h, knows);
  else if (r?.elder) elderBot(s, h, knows);
  // Chapter 4, «La Isla Blanca» (ai/isla-bot.ts).
  else return islaBot(s, h, knows);
  return true;
};
